import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchSpacePost } from '@core/actions/space/posts';
import { tryToSubscribeToSpaceLevelEvents } from '@core/actions/socket';
import { getPostById } from '@core/selectors/posts.selectors';
import { getComments } from '@core/selectors/comments';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import { accessingSpaceAsStaff } from '@core/selectors/classroom';
import { operations as reactionOperations } from '@app/store/reactions';
import { HOME_UPDATE_POST_COUNTS } from '@core/constants/actions';

import { getFullName, getUserInitials } from './recentUpdates.utils';
import PostDetailPopupView from './PostDetailPopupView';

export interface PostDetailPopupProps {
  item: any;
  open: boolean;
  onClose: () => void;
}

const PostDetailPopup: React.FC<PostDetailPopupProps> = ({
  item,
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const spaceId: string = item?.classRoom;
  const postId: string = item?.objectId || item?.id;

  const post = useSelector((state: any) =>
    postId ? getPostById(state, postId) : null
  );

  const comments = useSelector((state: any) =>
    postId ? getComments(state, postId, CommentsEntityTypes.post) : []
  );

  const isReadOnly = useSelector((state: any) =>
    spaceId ? accessingSpaceAsStaff(state)(spaceId) : false
  );

  const reactionsFromStore = useSelector((state: any) => {
    if (!postId) return [];
    const reactionMap = state.data?.reactions || {};
    return Object.values(reactionMap).filter((r: any) => r.itemId === postId);
  });

  const classRoomsMap = useSelector(
    (state: any) => state.data?.classRooms || {}
  );
  const matchedClassRoom = Object.values(classRoomsMap).find(
    (room: any) =>
      room.objectId === item?.classRoom || room.id === item?.classRoom
  ) as any;
  const classRoomName: string | undefined = matchedClassRoom?.name;
  const classRoomStartDate: string | undefined = matchedClassRoom?.startDate;
  const classRoomEndDate: string | undefined = matchedClassRoom?.endDate;

  useEffect(() => {
    if (open && postId && spaceId) {
      setIsLoading(true);
      const loadPost = async () => {
        try {
          await dispatch(fetchSpacePost(postId, true) as any);
          dispatch(tryToSubscribeToSpaceLevelEvents(spaceId) as any);
        } catch (err) {
          console.error('Failed to load post details', err);
        } finally {
          setIsLoading(false);
        }
      };
      loadPost();
    }
  }, [open, postId, spaceId, dispatch]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (postId) {
      const commentsCount = comments?.length || 0;
      const reactionsCount = reactionsFromStore.reduce(
        (sum: number, r: any) =>
          sum + (typeof r.count === 'number' ? r.count : 1),
        0
      );
      dispatch({
        type: HOME_UPDATE_POST_COUNTS,
        postId,
        commentsCount,
        reactionsCount,
      });
    }
    setShowComments(false);
    onClose();
  }, [postId, comments, reactionsFromStore, dispatch, onClose]);

  const toggleReaction = useCallback(
    ({ emojiName }: { emojiName: string }) =>
      dispatch(
        reactionOperations.toggleReaction({
          emojiName,
          itemId: postId,
          itemType: 'UBClassRoomPost',
        })
      ),
    [dispatch, postId]
  );

  const handleDidComment = useCallback(() => setShowComments(true), []);

  // ── Derived display values ─────────────────────────────────────────────────
  const fullName = getFullName(item?.user);
  const avatarUrl = item?.user?.avatar || '';
  const userInitials = getUserInitials(item?.user);

  return (
    <PostDetailPopupView
      open={open}
      onClose={handleClose}
      item={item}
      post={post}
      comments={comments}
      isLoading={isLoading}
      isReadOnly={isReadOnly}
      showComments={showComments}
      fullName={fullName}
      avatarUrl={avatarUrl}
      userInitials={userInitials}
      classRoomName={classRoomName}
      classRoomStartDate={classRoomStartDate}
      classRoomEndDate={classRoomEndDate}
      toggleReaction={toggleReaction}
      onDidComment={handleDidComment}
    />
  );
};

export default PostDetailPopup;
